import { SalesService } from './sales.service';
import { Lead } from '@/models/lead.model';

export interface ScrapedRestaurant {
  businessName: string;
  ownerName?: string;
  phone?: string;
  email?: string;
  website?: string;
  address: {
    street?: string;
    city: string;
    state: string;
    zipCode?: string;
  };
  restaurantType?: string;
  priceTier?: string;
  estimatedEmployees?: string;
  locations?: number;
  source: string;
  sourceUrl?: string;
  reviewCount?: number;
  rating?: number;
  licenseNumber?: string;
  posSystem?: string;
}

export class ScrapingService {
  /**
   * Convert scraped restaurant data to Lead format
   */
  static convertToLead(
    restaurant: ScrapedRestaurant,
    assignedTo: string
  ): Omit<Lead, 'id' | 'createdAt' | 'updatedAt'> {
    // New leads start as 'new'
    const status = 'new' as const;
    
    // Create company name
    const companyName = restaurant.businessName;
    
    // Create contact name (use owner name if available, otherwise business name)
    const contactName = restaurant.ownerName || restaurant.businessName;
    
    // Try to extract email from website or use placeholder
    let email = restaurant.email;
    if (!email && restaurant.website) {
      // Try to construct email from website domain
      try {
        const domain = new URL(restaurant.website.startsWith('http') ? restaurant.website : `https://${restaurant.website}`).hostname.replace('www.', '');
        email = `info@${domain}`;
      } catch {
        email = `contact@${restaurant.website}`;
      }
    }
    if (!email) {
      email = `contact@${restaurant.businessName.toLowerCase().replace(/\s+/g, '')}.com`;
    }
    
    // Build notes from scraped data
    const notes = this.buildNotes(restaurant);
    
    // Determine value based on estimated employees
    const value = this.estimateValue(restaurant.estimatedEmployees);
    
    return {
      companyName,
      contactName,
      email,
      phone: restaurant.phone,
      source: `Scraped: ${restaurant.source}`,
      status,
      assignedTo,
      value,
      notes,
    };
  }

  /**
   * Build notes from scraped restaurant data
   */
  private static buildNotes(restaurant: ScrapedRestaurant): string {
    const parts: string[] = [];
    
    parts.push(`Restaurant Type: ${restaurant.restaurantType || 'Unknown'}`);
    
    if (restaurant.priceTier) {
      parts.push(`Price Tier: ${restaurant.priceTier}`);
    }
    
    if (restaurant.estimatedEmployees) {
      parts.push(`Estimated Employees: ${restaurant.estimatedEmployees}`);
    }
    
    if (restaurant.locations) {
      parts.push(`Locations: ${restaurant.locations}`);
    }
    
    if (restaurant.rating) {
      parts.push(`Rating: ${restaurant.rating}/5`);
    }
    
    if (restaurant.reviewCount) {
      parts.push(`Reviews: ${restaurant.reviewCount}`);
    }
    
    if (restaurant.posSystem) {
      parts.push(`POS System: ${restaurant.posSystem}`);
    }
    
    if (restaurant.licenseNumber) {
      parts.push(`License: ${restaurant.licenseNumber}`);
    }
    
    if (restaurant.sourceUrl) {
      parts.push(`Source: ${restaurant.sourceUrl}`);
    }
    
    if (restaurant.address.city && restaurant.address.state) {
      parts.push(`Location: ${restaurant.address.city}, ${restaurant.address.state}`);
    }
    
    return parts.join('\n');
  }

  /**
   * Estimate lead value based on employee count
   */
  private static estimateValue(estimatedEmployees?: string): number {
    // Rough estimate: $50-100 per employee per month
    const estimates: Record<string, number> = {
      '1-10': 500,      // 5 employees * $100/month = $500/month = $6k/year
      '11-25': 1500,    // 18 employees * $100/month = $1.5k/month = $18k/year
      '26-50': 3000,    // 38 employees * $100/month = $3k/month = $36k/year
      '51-100': 6000,   // 75 employees * $100/month = $6k/month = $72k/year
      '100+': 12000,    // 150 employees * $100/month = $12k/month = $144k/year
    };
    
    return estimates[estimatedEmployees || '1-10'] || 500;
  }

  /**
   * Import bulk scraped restaurants as leads
   */
  static async importRestaurants(
    restaurants: ScrapedRestaurant[],
    assignedTo: string
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const restaurant of restaurants) {
      try {
        const lead = this.convertToLead(restaurant, assignedTo);
        await SalesService.createLead(lead);
        success++;
      } catch (error: any) {
        failed++;
        errors.push(`${restaurant.businessName}: ${error.message}`);
      }
    }

    return { success, failed, errors };
  }

  /**
   * Validate scraped restaurant data
   */
  static validateRestaurant(restaurant: ScrapedRestaurant): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!restaurant.businessName || restaurant.businessName.trim().length === 0) {
      errors.push('Business name is required');
    }

    if (!restaurant.address?.city) {
      errors.push('City is required');
    }

    if (!restaurant.address?.state) {
      errors.push('State is required');
    }

    if (!restaurant.source) {
      errors.push('Source is required');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Parse CSV/JSON import data
   */
  static parseImportData(data: string, format: 'csv' | 'json'): ScrapedRestaurant[] {
    if (format === 'json') {
      try {
        return JSON.parse(data);
      } catch (error) {
        throw new Error('Invalid JSON format');
      }
    } else {
      // CSV parsing
      return this.parseCSV(data);
    }
  }

  /**
   * Parse CSV data
   */
  private static parseCSV(csv: string): ScrapedRestaurant[] {
    const lines = csv.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const restaurants: ScrapedRestaurant[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const restaurant: any = {};

      headers.forEach((header, index) => {
        const value = values[index] || '';
        
        // Map common CSV headers to our model
        if (header.includes('business') || header.includes('name') || header.includes('restaurant')) {
          restaurant.businessName = value;
        } else if (header.includes('owner')) {
          restaurant.ownerName = value;
        } else if (header.includes('phone')) {
          restaurant.phone = value;
        } else if (header.includes('email')) {
          restaurant.email = value;
        } else if (header.includes('website')) {
          restaurant.website = value;
        } else if (header.includes('city')) {
          restaurant.address = restaurant.address || {};
          restaurant.address.city = value;
        } else if (header.includes('state')) {
          restaurant.address = restaurant.address || {};
          restaurant.address.state = value;
        } else if (header.includes('zip')) {
          restaurant.address = restaurant.address || {};
          restaurant.address.zipCode = value;
        } else if (header.includes('street') || header.includes('address')) {
          restaurant.address = restaurant.address || {};
          restaurant.address.street = value;
        } else if (header.includes('source')) {
          restaurant.source = value;
        } else if (header.includes('type')) {
          restaurant.restaurantType = value;
        } else if (header.includes('price') || header.includes('tier')) {
          restaurant.priceTier = value;
        } else if (header.includes('employee')) {
          restaurant.estimatedEmployees = value;
        } else if (header.includes('location') && !header.includes('address')) {
          restaurant.locations = parseInt(value) || 1;
        } else if (header.includes('review')) {
          restaurant.reviewCount = parseInt(value) || 0;
        } else if (header.includes('rating')) {
          restaurant.rating = parseFloat(value) || 0;
        } else if (header.includes('pos')) {
          restaurant.posSystem = value;
        } else if (header.includes('license')) {
          restaurant.licenseNumber = value;
        }
      });

      if (restaurant.businessName && restaurant.address?.city && restaurant.address?.state) {
        restaurants.push(restaurant as ScrapedRestaurant);
      }
    }

    return restaurants;
  }
}

